import React, { ChangeEvent, forwardRef, InputHTMLAttributes, ReactNode } from "react";
import cn from "classnames";
import styles from "./text-input.module.css";
import { CloseIcon } from "frontend/icons/close";
import { useId } from "@floating-ui/react";

export type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** Optional icon to display on the left side of the input */
  leftIcon?: ReactNode;
  /** Additional CSS class names */
  className?: string;
  /** Size variant of the input */
  variant?: "small" | "medium" | "large";
  /** Error state */
  error?: boolean;
  /** Full width */
  fullWidth?: boolean;

  clearable?: boolean;
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    leftIcon,
    className,
    variant = "medium",
    error = false,
    fullWidth = false,
    disabled,
    clearable = false,
    ...inputProps
  },
  ref
) {
  const id = useId();
  const value = (document.getElementById(id!) as HTMLInputElement)?.value as string;
  return (
    <div
      className={cn(
        styles.wrapper,
        styles[`size-${variant}`],
        {
          [styles.error]: error,
          [styles.disabled]: disabled,
          [styles.fullWidth]: fullWidth,
          [styles.hasIcon]: !!leftIcon,
        },
        className
      )}
    >
      {leftIcon && <div className={styles.iconWrapper}>{leftIcon}</div>}
      <input ref={ref} type="text" className={styles.input} disabled={disabled} {...inputProps} id={id} />
      {clearable && value && (
        <div
          className={styles.clearIcon}
          onClick={() => {
            if (id) {
              const input = document.getElementById(id) as HTMLInputElement;
              input.value = "";
            }
            const event = {
              target: {
                value: "",
              } as EventTarget & HTMLInputElement,
            };
            inputProps.onChange?.(event as ChangeEvent<HTMLInputElement>);
          }}
        >
          <CloseIcon />
        </div>
      )}
    </div>
  );
});
